import { isValid, parseISO } from "date-fns";
import { z } from "zod";
import {
  createEmptyPropertyRow,
  isCompletePropertyRow,
} from "@/utils/propertyRows";
import { PREFECTURES } from "@/utils/prefecture";
import {
  type ResearchRequestFormData,
  type ResearchRequestInitialPropertyData,
} from "../types";
import { splitPrefectureFromAddress } from "../utils/address";
import { isBusinessDay } from "../utils/businessDays";

// 件数の上限は、依頼 API（real-estate-research-request）の Serializer と同じ値にする
const MAX_PROPERTY_ROWS = 200;
const MAX_CUSTOM_DOCUMENTS = 20;

// 文字数の上限は、依頼 API（real-estate-research-request）の Serializer と同じ値にする
const maxLength = (max: number, label: string): z.ZodString =>
  z.string().max(max, `${label}は${max}文字以内で入力してください`);

const propertyRowSchema = z.object({
  prefName: maxLength(100, "都道府県"),
  location: maxLength(500, "所在"),
  chibanAndKaokuNumber: maxLength(200, "地番・家屋番号"),
});

const optionalNumber = z.number().optional();

const todayStart = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const realEstateResearchRequestSchema = z
  .object({
    addressPrefecture: z
      .string()
      .refine(
        (value) => PREFECTURES.some((prefecture) => prefecture.name === value),
        "都道府県を選択してください"
      ),
    address: maxLength(500, "対象住所").trim().min(1, "住所を入力してください"),
    chibanList: z
      .array(propertyRowSchema)
      .max(MAX_PROPERTY_ROWS, `地番は${MAX_PROPERTY_ROWS}件まで入力できます`)
      .refine(
        (rows) => rows.some(isCompletePropertyRow),
        "完全入力済みの地番を1件以上入力してください"
      ),
    kaokuNumberList: z
      .array(propertyRowSchema)
      .max(
        MAX_PROPERTY_ROWS,
        `家屋番号は${MAX_PROPERTY_ROWS}件まで入力できます`
      ),
    noKaokuNumber: z.boolean(),
    isIsland: z.boolean(),
    ownerName: maxLength(200, "不動産所有者名"),
    requestTypes: z
      .array(
        z.enum([
          "simple",
          "detailed",
          "sales_contract",
          "important_explanation_check",
          "custom_agreement",
        ])
      )
      .min(1, "依頼種別を選択してください"),
    onsitePhoto: z.boolean(),
    specialOptions: z.object({
      siteAreaSqm: optionalNumber,
      subdivisionUnits: optionalNumber,
      condominiumUnits: optionalNumber,
      registeredLots: optionalNumber,
    }),
    selectedDocuments: z.array(
      z.enum([
        "registry",
        "public_map",
        "land_survey_map",
        "building_drawing",
        "closed_registry",
      ])
    ),
    registryAdjacent: z.boolean(),
    registryAdjacentKind: z.enum(["", "full", "owner", "summary"]),
    customDocuments: z
      .array(maxLength(200, "その他の取得資料"))
      .max(
        MAX_CUSTOM_DOCUMENTS,
        `その他の取得資料は${MAX_CUSTOM_DOCUMENTS}件まで入力できます`
      ),
    specifyDeliveryDate: z.boolean(),
    desiredDeliveryDate: z
      .string()
      .refine(
        (value) => value === "" || !Number.isNaN(new Date(value).getTime()),
        "正しい日付を入力してください"
      )
      .refine((value) => {
        if (value === "") return true;
        const selectedDate = new Date(`${value}T00:00:00`);
        return selectedDate >= todayStart();
      }, "過去の日付は指定できません"),
    deadlineShortening: z.union([
      z.literal(0),
      z.literal(1),
      z.literal(2),
      z.literal(3),
    ]),
    requesterName: maxLength(100, "担当者名")
      .trim()
      .min(1, "担当者名を入力してください"),
    requesterEmail: z
      .string()
      .trim()
      .min(1, "担当者メールアドレスを入力してください")
      .email("正しいメールアドレスを入力してください"),
    requesterPhone: maxLength(30, "担当者電話番号")
      .trim()
      .min(1, "担当者電話番号を入力してください"),
    needsPostalDelivery: z.boolean(),
    postalAddress: maxLength(500, "郵送先"),
    notes: z
      .string()
      .max(1000, "補足・留意事項は1,000文字以内で入力してください"),
  })
  .superRefine((data, context) => {
    // 物件の規模は任意。入力があるときだけ、数値として正しいかを確かめる
    const sizeFields: Array<{
      value: number | undefined;
      path: keyof ResearchRequestFormData["specialOptions"];
      integer: boolean;
    }> = [
      {
        value: data.specialOptions.siteAreaSqm,
        path: "siteAreaSqm",
        integer: false,
      },
      {
        value: data.specialOptions.subdivisionUnits,
        path: "subdivisionUnits",
        integer: true,
      },
      {
        value: data.specialOptions.condominiumUnits,
        path: "condominiumUnits",
        integer: true,
      },
      {
        value: data.specialOptions.registeredLots,
        path: "registeredLots",
        integer: true,
      },
    ];

    sizeFields.forEach(({ value, path, integer }) => {
      if (value === undefined) return;
      const message = Number.isNaN(value)
        ? "数値を入力してください"
        : value <= 0
        ? "0より大きい数値を入力してください"
        : // 依頼 API の敷地面積の下限（0.01㎡）とそろえる
        !integer && value < 0.01
        ? "0.01以上の数値を入力してください"
        : integer && !Number.isInteger(value)
        ? "整数を入力してください"
        : null;
      if (message) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ["specialOptions", path],
        });
      }
    });

    const addIssue = (path: string, message: string): void => {
      context.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
    };

    if (
      data.registryAdjacent &&
      data.selectedDocuments.includes("registry") &&
      data.registryAdjacentKind === ""
    ) {
      addIssue(
        "registryAdjacentKind",
        "隣接地の登記簿の種類を選択してください"
      );
    }

    if (data.specifyDeliveryDate && data.desiredDeliveryDate === "") {
      addIssue("desiredDeliveryDate", "納期の日付を選択してください");
    } else if (
      data.specifyDeliveryDate &&
      isValid(parseISO(data.desiredDeliveryDate)) &&
      !isBusinessDay(parseISO(data.desiredDeliveryDate))
    ) {
      addIssue(
        "desiredDeliveryDate",
        "土日祝・年末年始（12/29〜1/3）は納期に指定できません"
      );
    }

    if (data.needsPostalDelivery && data.postalAddress.trim() === "") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "郵送先を入力してください",
        path: ["postalAddress"],
      });
    }
  });

export const createDefaultResearchRequestFormData = (
  initialPropertyData?: ResearchRequestInitialPropertyData
): ResearchRequestFormData => {
  const splitAddress = splitPrefectureFromAddress(
    initialPropertyData?.address ?? ""
  );
  // 地番行の都道府県が空なら、家屋番号行の都道府県を使う
  const propertyPrefecture =
    [
      ...(initialPropertyData?.chibanList ?? []),
      ...(initialPropertyData?.kaokuNumberList ?? []),
    ]
      .map((row) => row.prefName)
      .find((prefName) => prefName.trim() !== "") ?? "";
  const initialChibanList = initialPropertyData?.chibanList ?? [];
  const initialKaokuNumberList = initialPropertyData?.kaokuNumberList ?? [];

  return {
    addressPrefecture: splitAddress.addressPrefecture || propertyPrefecture,
    address: splitAddress.address,
    // 建物から開いたときなど地番が無くても、入力できるよう空行を1件出す
    chibanList:
      initialChibanList.length > 0
        ? initialChibanList.map((row) => ({ ...row }))
        : [createEmptyPropertyRow()],
    kaokuNumberList:
      initialKaokuNumberList.length > 0
        ? initialKaokuNumberList.map((row) => ({ ...row }))
        : [createEmptyPropertyRow()],
    noKaokuNumber: false,
    isIsland: false,
    ownerName: "",
    requestTypes: [],
    onsitePhoto: false,
    specialOptions: {},
    selectedDocuments: [],
    registryAdjacent: false,
    registryAdjacentKind: "",
    customDocuments: [""],
    specifyDeliveryDate: false,
    desiredDeliveryDate: "",
    deadlineShortening: 0,
    requesterName: "",
    requesterEmail: "",
    requesterPhone: "",
    needsPostalDelivery: false,
    postalAddress: "",
    notes: "",
  };
};
